import type { Address, Hex, PreparedCall } from '@hoodmorning/contracts';
import { decodeAbiParameters, decodeFunctionData, encodeAbiParameters, encodeFunctionData, getAddress, isAddressEqual, parseAbi, parseAbiParameters, zeroAddress } from 'viem';

/**
 * Pinned local dependency identities, not a chain/deployment attestation.
 * `@uniswap/universal-router-sdk@5.11.3` depends on
 * `@uniswap/universal-router@2.1.0` in this repository. The latter's V2
 * dispatcher reads the five-field V2 exact-input payload below. In particular,
 * it is not the SDK's newer 2.1.1 six-field minHopPriceX36 layout.
 */
export const LAUNCH_INPUT_CONVERSION_SCHEMA_VERSION = 1 as const;
export const LAUNCH_INPUT_CONVERSION_ROUTER_IMPLEMENTATION_VERSION = '2.1.0' as const;
export const LAUNCH_INPUT_CONVERSION_ROUTER_SDK_VERSION = '5.11.3' as const;

const MAX_UINT48 = (1n << 48n) - 1n;
const MAX_UINT160 = (1n << 160n) - 1n;
const MAX_UINT256 = (1n << 256n) - 1n;
const MAX_DEADLINE_WINDOW_SECONDS = 300n;
const V2_EXACT_INPUT_COMMAND = '0x08' as const;

const erc20ApproveAbi = parseAbi(['function approve(address spender,uint256 amount) returns(bool)']);
const permit2ApproveAbi = parseAbi(['function approve(address token,address spender,uint160 amount,uint48 expiration)']);
const universalRouterExecuteAbi = parseAbi(['function execute(bytes commands,bytes[] inputs,uint256 deadline) payable']);
const v2ExactInputParameters = parseAbiParameters('address recipient,uint256 amountIn,uint256 amountOutMin,address[] path,bool payerIsUser');

export type LaunchInputConversionRequest = Readonly<{
  schemaVersion: typeof LAUNCH_INPUT_CONVERSION_SCHEMA_VERSION;
  ownerVault: Address;
  tokenIn: Address;
  approvedQuote: Address;
  /** This codec supports exactly one V2 pool: [tokenIn, approvedQuote]. */
  path: readonly Address[];
  amountIn: bigint;
  minimumQuoteOut: bigint;
  deadlineUnixSeconds: bigint;
  nowUnixSeconds: bigint;
  router: Address;
  permit2: Address;
}>;

export type LaunchInputConversion = Readonly<{
  schemaVersion: typeof LAUNCH_INPUT_CONVERSION_SCHEMA_VERSION;
  routerImplementationVersion: typeof LAUNCH_INPUT_CONVERSION_ROUTER_IMPLEMENTATION_VERSION;
  routerSdkVersion: typeof LAUNCH_INPUT_CONVERSION_ROUTER_SDK_VERSION;
  ownerVault: Address;
  tokenIn: Address;
  approvedQuote: Address;
  amountIn: bigint;
  minimumQuoteOut: bigint;
  deadlineUnixSeconds: bigint;
  /** No conversion-specific HoodMorning or referral fee is encoded. */
  hoodMorningFeeBps: 0;
  referralFeeBps: 0;
  calls: readonly PreparedCall[];
  conversionCallIndex: 3;
  cleanupCallIndexes: readonly [4, 5];
}>;

type NormalizedRequest = Omit<LaunchInputConversionRequest, 'path'> & {
  ownerVault: Address;
  tokenIn: Address;
  approvedQuote: Address;
  router: Address;
  permit2: Address;
  path: readonly [Address, Address];
};

function address(value: string, label: string): Address {
  let result: Address;
  try {
    result = getAddress(value);
  } catch {
    throw new Error(`Invalid ${label} address`);
  }
  if (isAddressEqual(result, zeroAddress)) throw new Error(`Zero ${label} address is not supported`);
  return result;
}

function exactPositiveUint(value: unknown, maximum: bigint, label: string): bigint {
  if (typeof value !== 'bigint' || value <= 0n || value > maximum) throw new Error(`Invalid ${label}`);
  return value;
}

function normalize(input: LaunchInputConversionRequest): NormalizedRequest {
  if (input.schemaVersion !== LAUNCH_INPUT_CONVERSION_SCHEMA_VERSION) throw new Error('Unsupported launch input conversion schema');
  if (typeof input.nowUnixSeconds !== 'bigint' || input.nowUnixSeconds < 0n) throw new Error('Invalid conversion clock');
  const ownerVault = address(input.ownerVault, 'owner vault');
  const tokenIn = address(input.tokenIn, 'input token');
  const approvedQuote = address(input.approvedQuote, 'approved quote');
  const router = address(input.router, 'router');
  const permit2 = address(input.permit2, 'Permit2');
  const roles = [ownerVault, tokenIn, approvedQuote, router, permit2];
  if (new Set(roles.map(value => value.toLowerCase())).size !== roles.length) {
    throw new Error('Conversion roles must be distinct');
  }
  if (!Array.isArray(input.path) || input.path.length !== 2) throw new Error('Only a single-pool V2 path is supported');
  const path = [address(input.path[0]!, 'path token'), address(input.path[1]!, 'path token')] as const;
  if (!isAddressEqual(path[0], tokenIn) || !isAddressEqual(path[1], approvedQuote)) throw new Error('Conversion path does not match approved assets');
  const amountIn = exactPositiveUint(input.amountIn, MAX_UINT160 - 1n, 'exact input amount');
  const minimumQuoteOut = exactPositiveUint(input.minimumQuoteOut, MAX_UINT256, 'minimum quote output');
  const deadlineUnixSeconds = exactPositiveUint(input.deadlineUnixSeconds, MAX_UINT48, 'conversion deadline');
  if (deadlineUnixSeconds <= input.nowUnixSeconds || deadlineUnixSeconds - input.nowUnixSeconds > MAX_DEADLINE_WINDOW_SECONDS) {
    throw new Error('Conversion deadline must be within five minutes');
  }
  return { ...input, ownerVault, tokenIn, approvedQuote, router, permit2, path, amountIn, minimumQuoteOut, deadlineUnixSeconds };
}

function canonicalErc20Approval(data: Hex, label: string) {
  try {
    const decoded = decodeFunctionData({ abi: erc20ApproveAbi, data });
    if (decoded.functionName !== 'approve' || !decoded.args) throw new Error();
    const canonical = encodeFunctionData({ abi: erc20ApproveAbi, functionName: 'approve', args: decoded.args });
    if (canonical.toLowerCase() !== data.toLowerCase()) throw new Error(`Noncanonical ${label} calldata`);
    return decoded.args;
  } catch {
    throw new Error(`Invalid ${label} calldata`);
  }
}

function canonicalPermit2Approval(data: Hex, label: string) {
  try {
    const decoded = decodeFunctionData({ abi: permit2ApproveAbi, data });
    if (decoded.functionName !== 'approve' || !decoded.args) throw new Error();
    const canonical = encodeFunctionData({ abi: permit2ApproveAbi, functionName: 'approve', args: decoded.args });
    if (canonical.toLowerCase() !== data.toLowerCase()) throw new Error(`Noncanonical ${label} calldata`);
    return decoded.args;
  } catch {
    throw new Error(`Invalid ${label} calldata`);
  }
}

function canonicalRouterExecute(data: Hex) {
  try {
    const decoded = decodeFunctionData({ abi: universalRouterExecuteAbi, data });
    if (decoded.functionName !== 'execute' || !decoded.args) throw new Error();
    const canonical = encodeFunctionData({ abi: universalRouterExecuteAbi, functionName: 'execute', args: decoded.args });
    if (canonical.toLowerCase() !== data.toLowerCase()) throw new Error('Noncanonical Universal Router conversion calldata');
    return decoded.args;
  } catch {
    throw new Error('Invalid Universal Router conversion calldata');
  }
}

function requireCall(call: PreparedCall | undefined, target: Address, label: string) {
  if (!call || call.value !== 0n || !isAddressEqual(call.to, target)) throw new Error(`Invalid ${label} call target or value`);
  return call;
}

function buildResult(expected: NormalizedRequest, calls: readonly PreparedCall[]): LaunchInputConversion {
  return {
    schemaVersion: LAUNCH_INPUT_CONVERSION_SCHEMA_VERSION,
    routerImplementationVersion: LAUNCH_INPUT_CONVERSION_ROUTER_IMPLEMENTATION_VERSION,
    routerSdkVersion: LAUNCH_INPUT_CONVERSION_ROUTER_SDK_VERSION,
    ownerVault: expected.ownerVault,
    tokenIn: expected.tokenIn,
    approvedQuote: expected.approvedQuote,
    amountIn: expected.amountIn,
    minimumQuoteOut: expected.minimumQuoteOut,
    deadlineUnixSeconds: expected.deadlineUnixSeconds,
    hoodMorningFeeBps: 0,
    referralFeeBps: 0,
    calls: calls.map(call => ({ ...call })),
    conversionCallIndex: 3,
    cleanupCallIndexes: [4, 5],
  };
}

/**
 * Encode the conversion-only prefix for an atomic deployed-vault launch batch.
 * It deliberately contains no launch call, fee/referral transfer, route lookup,
 * allowance observation, simulation, source/pool/router provenance proof, output
 * receipt accounting, or submission authority. Those remain integration gates.
 */
export function encodeLaunchInputConversion(input: LaunchInputConversionRequest): LaunchInputConversion {
  const expected = normalize(input);
  const swapInput = encodeAbiParameters(v2ExactInputParameters, [
    expected.ownerVault,
    expected.amountIn,
    expected.minimumQuoteOut,
    [...expected.path],
    true,
  ]);
  const calls: PreparedCall[] = [
    { to: expected.tokenIn, value: 0n, data: encodeFunctionData({ abi: erc20ApproveAbi, functionName: 'approve', args: [expected.permit2, 0n] }) },
    { to: expected.tokenIn, value: 0n, data: encodeFunctionData({ abi: erc20ApproveAbi, functionName: 'approve', args: [expected.permit2, expected.amountIn] }) },
    { to: expected.permit2, value: 0n, data: encodeFunctionData({ abi: permit2ApproveAbi, functionName: 'approve', args: [expected.tokenIn, expected.router, expected.amountIn, Number(expected.deadlineUnixSeconds)] }) },
    { to: expected.router, value: 0n, data: encodeFunctionData({ abi: universalRouterExecuteAbi, functionName: 'execute', args: [V2_EXACT_INPUT_COMMAND, [swapInput], expected.deadlineUnixSeconds] }) },
    { to: expected.permit2, value: 0n, data: encodeFunctionData({ abi: permit2ApproveAbi, functionName: 'approve', args: [expected.tokenIn, expected.router, 0n, Number(expected.deadlineUnixSeconds)] }) },
    { to: expected.tokenIn, value: 0n, data: encodeFunctionData({ abi: erc20ApproveAbi, functionName: 'approve', args: [expected.permit2, 0n] }) },
  ];
  return decodeAndValidateLaunchInputConversion({ expected, calls });
}

/**
 * Verify an untrusted proposed conversion prefix against exact expected values.
 * The six-call shape is intentional: reset ERC20 -> exact ERC20 -> exact Permit2
 * -> one non-revert V2 swap -> zero Permit2 -> zero ERC20. It rejects all other
 * router commands, including sweeps, transfers, fee payments and subplans.
 */
export function decodeAndValidateLaunchInputConversion(input: {
  expected: LaunchInputConversionRequest;
  calls: readonly PreparedCall[];
}): LaunchInputConversion {
  const expected = normalize(input.expected);
  const calls = input.calls.map(call => ({ ...call }));
  if (calls.length !== 6) throw new Error('Conversion prefix must contain exactly six calls');

  const erc20Reset = canonicalErc20Approval(requireCall(calls[0], expected.tokenIn, 'initial ERC20 reset').data, 'initial ERC20 reset');
  const erc20Exact = canonicalErc20Approval(requireCall(calls[1], expected.tokenIn, 'exact ERC20 approval').data, 'exact ERC20 approval');
  const permit2Exact = canonicalPermit2Approval(requireCall(calls[2], expected.permit2, 'exact Permit2 approval').data, 'exact Permit2 approval');
  const swapCall = requireCall(calls[3], expected.router, 'Universal Router conversion');
  const permit2Cleanup = canonicalPermit2Approval(requireCall(calls[4], expected.permit2, 'Permit2 cleanup').data, 'Permit2 cleanup');
  const erc20Cleanup = canonicalErc20Approval(requireCall(calls[5], expected.tokenIn, 'ERC20 cleanup').data, 'ERC20 cleanup');

  if (!isAddressEqual(erc20Reset[0], expected.permit2) || erc20Reset[1] !== 0n
    || !isAddressEqual(erc20Exact[0], expected.permit2) || erc20Exact[1] !== expected.amountIn
    || !isAddressEqual(erc20Cleanup[0], expected.permit2) || erc20Cleanup[1] !== 0n) {
    throw new Error('ERC20 conversion approvals must reset, grant the exact input, then reset');
  }
  if (!isAddressEqual(permit2Exact[0], expected.tokenIn) || !isAddressEqual(permit2Exact[1], expected.router)
    || permit2Exact[2] !== expected.amountIn || BigInt(permit2Exact[3]) !== expected.deadlineUnixSeconds
    || !isAddressEqual(permit2Cleanup[0], expected.tokenIn) || !isAddressEqual(permit2Cleanup[1], expected.router)
    || permit2Cleanup[2] !== 0n || BigInt(permit2Cleanup[3]) !== expected.deadlineUnixSeconds) {
    throw new Error('Permit2 conversion permissions must be exact, deadline-bound, and cleaned up');
  }

  const [commands, routerInputs, deadline] = canonicalRouterExecute(swapCall.data);
  if (commands.toLowerCase() !== V2_EXACT_INPUT_COMMAND || routerInputs.length !== 1 || deadline !== expected.deadlineUnixSeconds) {
    throw new Error('Conversion must contain exactly one non-revert V2 exact-input command');
  }
  const swapPayload = routerInputs[0];
  if (!swapPayload) throw new Error('Missing V2 exact-input payload');
  let swap: readonly [Address, bigint, bigint, readonly Address[], boolean];
  try {
    swap = decodeAbiParameters(v2ExactInputParameters, swapPayload);
  } catch {
    throw new Error('Invalid V2 exact-input payload');
  }
  if (encodeAbiParameters(v2ExactInputParameters, swap).toLowerCase() !== swapPayload.toLowerCase()) {
    throw new Error('Noncanonical V2 exact-input payload');
  }
  const [recipient, amountIn, amountOutMinimum, path, payerIsUser] = swap;
  if (!isAddressEqual(recipient, expected.ownerVault) || amountIn !== expected.amountIn || amountOutMinimum !== expected.minimumQuoteOut
    || !payerIsUser || path.length !== 2 || !isAddressEqual(path[0]!, expected.tokenIn) || !isAddressEqual(path[1]!, expected.approvedQuote)) {
    throw new Error('V2 exact-input payload does not match the approved conversion');
  }
  return buildResult(expected, calls);
}
